import { type ClassValue, clsx } from 'clsx';
import { formatDistance } from 'date-fns';
import Handlebars from 'handlebars';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export function humanizeBytes(bytes: number) {
	if (bytes === 0) {
		return '0 B';
	}

	// max(1, ...) to always show at least kB
	const i = Math.max(1, Math.floor(Math.log(bytes) / Math.log(1024)));
	const size = (bytes / 1024 ** i).toFixed(1);
	return `${size} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
}

export function slugify(s: string) {
	const words = s.split(' ');
	return words.map((w) => w.toLowerCase()).join('-');
}

export const randomId = () => Math.random().toString(36).slice(2);

export function trimDecimals(num: number) {
	if (num === 0) {
		return '0';
	}
	const requiredDecimals = 1 + Math.ceil(-Math.log10(num));
	const decimals = Math.min(9, Math.max(1, requiredDecimals));
	return num.toFixed(decimals);
}

export function formatDecimalSeparator(num: number) {
	if (num === 0) {
		return '0';
	}

	const i = Math.max(1, Math.floor(Math.log(num) / Math.log(1000)));
	const size = (num / 1000 ** i).toFixed(0);
	const suffix = ['', 'k', 'M', 'G', 'T'][i];
	return `${size} ${suffix}`;
}

export function interpolatePrompt(prompt: string, context: Record<string, unknown>) {
	const template = Handlebars.compile(prompt);
	return template({
		...context,
		date: new Date().toISOString().split('T')[0]
	});
}

export function relativeTimeDistance(date: Date) {
	const result = formatDistance(date, new Date(), { addSuffix: true });
	return result.replaceAll('about ', '');
}

export function assertUnreachable(value: never): never {
	throw new Error('Unreachable assertion failed.', { cause: value });
}

export function compareSemVer(curr: string, other: string) {
	const thisParts = curr.split('.');
	const otherParts = other.split('.');
	const maxLen = Math.max(thisParts.length, otherParts.length);

	for (let i = 0; i < maxLen; i++) {
		const thisPart = thisParts[i];
		const otherPart = otherParts[i];

		if (thisPart < otherPart) {
			return -1;
		}
		if (thisPart > otherPart) {
			return 1;
		}
	}

	return 0;
}
